/**
 * usePasswordResetClient — shared client hook returning the 4 password-reset
 * callbacks pre-bound to a given API base path.
 *
 * Replaces the per-app inline `useMemo` callbacks pattern that was duplicated
 * across every LoginPage. Each consumer shrinks from ~30 lines of fetch
 * closures to a 2-line hook call:
 *
 *   const reset = usePasswordResetClient({ basePath: '/api/v1/auth/tenant/password-reset' });
 *
 *   <LoginShell {...reset} ... />
 *
 * The reset modal OWNS its captcha widget and hands the token to the two requests the router's CAPTCHA gate judges
 * (PLANNER-PALM-UAT-2026-11-03-001 finish gate 7c); a host brings no widget and no ref.
 *
 * Contract per `PasswordResetModal.jsx` (all four REQUIRED):
 *   - onPasswordResetInitiate(email, subdomain, captchaToken) → Promise<any>
 *   - onPasswordResetVerify(email, subdomain, code) → Promise<{resetToken}>
 *   - onPasswordResetComplete(resetToken, newPassword) → Promise<any>
 *   - onPasswordResetResend(email, subdomain, captchaToken) → Promise<any>
 *
 * Response envelope unwrap: the hook returns `json.data` — ONE placement. This paragraph used to
 * say `json.data ?? json` (and `data?.data ?? data` on an apiClient branch); both fallbacks and
 * that whole branch are gone. The shared response-helpers wrap every server response as
 * `{success, data}`, so a body without `data` is off-contract, not a second shape to accommodate.
 *
 * Errors: non-2xx responses, and a 2xx whose body is not JSON, throw an Error in the fleet vocabulary — `{status, errorCode, errorDetails, data}`
 * populated. The message is extracted from `json.message` /
 * `json.error.message` / `json.error` (string) / 'Request failed' fallback.
 *
 * Network errors (fetch rejection) are wrapped with a friendly message and
 * the original error preserved on `err.cause`.
 *
 * @module @puffin-core/login-shell/hooks/usePasswordResetClient
 */

import { useMemo } from 'react';

// The ONE lazy `globalThis.fetch` binding (RES-CLIENT-001 `Q-TRANSPORT-DISEAM`), by its dependency-free
// SUBPATH, never the barrel: the barrel imports axios, and this hook is on every app's login page.
import { createFetchSeam } from '@puffin-core/api-client/fetch-seam';

import { readJsonBody } from '../utils/readJsonBody.js';

/**
 * Build the 4 pre-bound callbacks for a given API base path.
 *
 * @param {Object} [options]
 * @param {string} options.basePath             Required. Base path for the 4 endpoints
 *                                              (no trailing slash). e.g.
 *                                              '/api/v1/auth/tenant/password-reset'.
 * @param {Function} [options.fetchImpl]        Override fetch impl (for testing).
 *                                              Defaults to the shared fetch seam,
 *                                              `createFetchSeam({ label: 'usePasswordResetClient' })`.
 * @param {Function} [options.getHeaders]       `() => Object`. Extra request headers, read
 *                                              per request — the storefront's dev-only
 *                                              tenant hint (`X-Tenant-ID` on a loopback
 *                                              host, RLT-Q40 b). They never replace the
 *                                              JSON content headers.
 * @returns {{
 *   onPasswordResetInitiate: (email: string, subdomain: string, captchaToken: string|null) => Promise<any>,
 *   onPasswordResetVerify: (email: string, subdomain: string, code: string) => Promise<any>,
 *   onPasswordResetComplete: (resetToken: string, newPassword: string) => Promise<any>,
 *   onPasswordResetResend: (email: string, subdomain: string, captchaToken: string|null) => Promise<any>,
 * }} `/initiate` and `/resend` carry the modal's captcha token when it has one; `/verify` and `/complete` are not gated
 *   and carry none.
 */
export function usePasswordResetClient(options = {}) {
  // 🗑️ `apiClient` is DELETED from this hook's options — RES-CLIENT-001 Part 6, 2026-09-01.
  //
  // Measured, not assumed: **not one of the eight `usePasswordResetClient` call sites passes it**
  // (console · palm-web · planner-web · payday-web · patrol-web · port-web · prep-web ·
  // purchase-web LoginPages), so the axios branch was exercised only by its own tests.
  //
  // And it was wrong by construction, which is why this is a prune rather than a conversion: this
  // hook runs on the LOGIN page, BEFORE authentication. An axios client carrying bearer + refresh
  // interceptors is the wrong transport for a pre-auth surface; `fetch` with
  // `credentials: 'include'` is the right one, and it is what every app actually used.
  //
  // Deleting it also removes one half of a DUAL-TRANSPORT pair — two code paths that had to be
  // kept in agreement by hand, and whose disagreement nothing could detect. That is the third
  // instance of the same shape in this wave (`comments-ui`, `useAuthenticatorConfig`, here).
  const { basePath, fetchImpl, getHeaders } = options;

  return useMemo(() => {
    if (typeof basePath !== 'string' || basePath.length === 0) {
      throw new Error(
        'usePasswordResetClient requires a `basePath` string option (e.g. "/api/v1/auth/tenant/password-reset").',
      );
    }

    // SEAM-001 B2a: the default was the bare `globalThis.fetch`, whose absence failed as
    // `fetchFn is not a function`. The seam reads the global when a request is SENT (a suite that
    // stubs late still wins) and rejects NAMING this hook when there is none.
    const fetchFn = fetchImpl || createFetchSeam({ label: 'usePasswordResetClient' });

    const post = async (path, body) => {
      const extraHeaders = typeof getHeaders === 'function' ? getHeaders() : null;
      let response;
      try {
        response = await fetchFn(`${basePath}${path}`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            ...extraHeaders,
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(body),
        });
      } catch (networkError) {
        const err = new Error('Network error. Please check your connection and try again.');
        err.cause = networkError;
        throw err;
      }

      const json = await readJsonBody(response);

      // A 2xx whose body is not a readable envelope is REFUSED like a non-2xx (SWALLOWED-CATCH-
      // BURNDOWN-001 U3) — it used to read as `{}` and resolve `undefined`.
      if (!response.ok || json === null) {
        // The fleet vocabulary (AUTH-ERROR-VOCABULARY-001): status · message · errorCode ·
        // errorDetails · data, read from the ONE wire shape — the RES-ENV-001 error envelope.
        // The bare-string `error` / top-level `message` arms were retired with it.
        const err = new Error(json?.error?.message || 'Request failed');
        err.status = response.status;
        err.errorCode = typeof json?.error?.code === 'string' ? json.error.code : null;
        err.errorDetails = json?.error?.details ?? null;
        err.data = json;
        throw err;
      }

      // ONE placement. Worth reading twice for the asymmetry above it:
      // an unparseable body used to read as `{}`, so the retired `?? json` arm made an unreadable
      // 200 return `{}` — indistinguishable from a successful reset that simply carried no fields.
      // An unreadable body is now refused above, and an off-contract ENVELOPE surfaces as
      // `undefined` rather than as a plausible-looking success object.
      //
      // `/verify` answers `sendSuccess(res, { resetToken })` and `onPasswordResetComplete` reads
      // `resetToken` off this return, so a fallthrough would break the reset at its LAST step with
      // an error pointing nowhere near the cause. All four routes (`/initiate` `/verify`
      // `/complete` `/resend`) were read: every one uses `sendSuccess`.
      return json.data;
    };

    // A request the router's CAPTCHA gate judges carries the modal's token when it has one. The modal holds the request
    // until a required token exists and resets its widget once the request settles (the token is single-use).
    const postGated = (path, body, captchaToken) =>
      post(path, captchaToken ? { ...body, captchaToken } : body);

    return {
      onPasswordResetInitiate: (email, subdomain, captchaToken) =>
        postGated('/initiate', { email, subdomain }, captchaToken),
      onPasswordResetVerify: (email, subdomain, code) =>
        post('/verify', { email, subdomain, code }),
      onPasswordResetComplete: (resetToken, newPassword) =>
        post('/complete', { resetToken, newPassword }),
      onPasswordResetResend: (email, subdomain, captchaToken) =>
        postGated('/resend', { email, subdomain }, captchaToken),
    };
  }, [basePath, fetchImpl, getHeaders]);
}

export default usePasswordResetClient;
